<script lang="ts" setup>
export interface MapModalProps {
    marker: any
}

export interface MapModalEmits {
    (e: 'close'): void
}

const { $t } = useNuxtApp()
const emit = defineEmits<MapModalEmits>()
const modalContainerRef = ref<HTMLElement>()
const closeButtonRef = ref<HTMLElement>()

// WCAG 2.4.3: Focus management - move focus to modal when it opens
// Google Maps InfoWindow renders asynchronously and may try to steal focus
let focusTrapActive = true

onMounted(() => {
    const focusCloseButton = () => {
        if (closeButtonRef.value && focusTrapActive) {
            closeButtonRef.value.focus()
        }
    }

    // Multiple delayed attempts - Google Maps is aggressive with focus
    setTimeout(focusCloseButton, 50)
    setTimeout(focusCloseButton, 150)
    setTimeout(focusCloseButton, 300)
    setTimeout(focusCloseButton, 500)

    // Add focusin listener to trap focus within the modal
    document.addEventListener('focusin', handleFocusIn, true)
})

onUnmounted(() => {
    focusTrapActive = false
    document.removeEventListener('focusin', handleFocusIn, true)
})

// WCAG 2.4.3: Trap focus within the modal - aggressively
const handleFocusIn = (event: FocusEvent): void => {
    if (!modalContainerRef.value || !focusTrapActive) return

    const target = event.target as HTMLElement

    // If focus went outside the modal container, bring it back
    if (!modalContainerRef.value.contains(target)) {
        event.preventDefault()
        event.stopPropagation()
        // Small delay to let Google Maps finish its focus manipulation
        setTimeout(() => {
            closeButtonRef.value?.focus()
        }, 10)
    }
}

// Handle keyboard navigation within modal (WCAG 2.1.1, 2.4.3)
const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
        event.preventDefault()
        emit('close')
        return
    }

    // Trap Tab within modal
    if (event.key === 'Tab' && modalContainerRef.value) {
        const focusableElements = modalContainerRef.value.querySelectorAll<HTMLElement>(
            'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
        const focusableArray = Array.from(focusableElements)

        if (focusableArray.length === 0) return

        const firstElement = focusableArray[0]
        const lastElement = focusableArray[focusableArray.length - 1]
        const activeElement = document.activeElement

        if (event.shiftKey && activeElement === firstElement) {
            event.preventDefault()
            lastElement?.focus()
        } else if (!event.shiftKey && activeElement === lastElement) {
            event.preventDefault()
            firstElement?.focus()
        }
    }
}
</script>
<template>
  <div
    ref="modalContainerRef"
    class="map-modal w-100"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    :aria-label="$t('Informácie o pobočke')"
    @keydown="handleKeydown"
  >
    <div class="bg-white rounded p-4 p-md-6 position-relative">
      <!-- Screen reader announcement for popup content -->
      <div
        class="visually-hidden"
        role="status"
        aria-live="polite"
      >
        {{ $t('Otvorené informácie o pobočke. Použite Tab pre navigáciu.') }}
      </div>
      <slot />
      <button
        ref="closeButtonRef"
        class="position-absolute top-0 end-0 z-index-10 bg-white border-0 d-block mt-3 me-3"
        :aria-label="$t('Zavrieť informácie o pobočke')"
        @click.prevent="emit('close')"
      >
        <svg
          class="icon m-"
          width="15"
          height="15"
          aria-hidden="true"
        >
          <use xlink:href="#times" />
        </svg>
      </button>
    </div>
  </div>
</template>
<style lang="scss">
@import './MapModal.scss';
</style>
